import { GENERIC_PROFILE_AVATAR_SRC } from '../constants/homePageContent';
import { wirePathToImageDisplaySrc } from './assetImageResolve';
import type { SocialPostRecord, SocialPostUserGroup } from './socialPostsStorage';
import { isOwnerLogoImageUrl } from './profileWorkerIdentity';
import { loadLocalProfileAvatarSrc } from './profileAvatarStorage';

/** True when a URL is story content (GCS …/stories/…/image) — never use as a profile chip. */
export function isStoryMediaUrl(src: string | null | undefined): boolean {
  if (!src?.trim()) {
    return false;
  }
  const s = src.trim();
  if (/\/stories\/[^/?#]+\/[^/?#]+\/(image|video|audio)/i.test(s)) {
    return true;
  }
  return /\/stories\/[^/?#]+\/[^/?#]+/i.test(s) && !/\/profiles\//i.test(s);
}

export function resolveProfileAvatarWireSrc(raw: string | null | undefined): string | null {
  if (!raw?.trim()) {
    return null;
  }
  const trimmed = raw.trim();
  if (isStoryMediaUrl(trimmed)) {
    return null;
  }
  if (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.startsWith('//') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed.startsWith('//') ? `https:${trimmed}` : trimmed;
  }
  return wirePathToImageDisplaySrc(trimmed) ?? trimmed;
}

export function resolveProfileAvatarDisplay(
  apiAvatarSrc: string | null | undefined,
  localAvatarSrc: string | null = loadLocalProfileAvatarSrc(),
): string {
  const local = resolveProfileAvatarWireSrc(localAvatarSrc);
  const api = resolveProfileAvatarWireSrc(apiAvatarSrc ?? null);
  const localSafe = local && isOwnerLogoImageUrl(local) ? null : local;
  const apiSafe = api && isOwnerLogoImageUrl(api) ? null : api;

  // Pending user crop (data URL) — keep until PATCH /profile confirms the upload.
  if (localSafe?.startsWith('data:image/')) {
    return localSafe;
  }

  // Server photo syncs across browsers; device-only URL cache must not win.
  if (apiSafe) {
    return apiSafe;
  }

  if (localSafe) {
    return localSafe;
  }
  return GENERIC_PROFILE_AVATAR_SRC;
}

function storyUserIdsMatch(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** First letter(s) for a story chip when the API has no profile photo. */
export function storyAvatarInitialsFromLabel(label: string): string {
  const cleaned = label.replace(/\s+/g, ' ').trim();
  if (!cleaned) {
    return '?';
  }
  const parts = cleaned.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0] ?? '';
    const b = parts[1][0] ?? '';
    return `${a}${b}`.toLocaleUpperCase();
  }
  return cleaned.slice(0, 2).toLocaleUpperCase();
}

/** SVG data-URL with initials — used when stories API returns no `userAvatarSrc`. */
export function buildStoryInitialsAvatarSrc(label: string): string {
  const initials = storyAvatarInitialsFromLabel(label)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">` +
    `<rect width="200" height="200" fill="#623DFF"/>` +
    `<text x="100" y="100" dy="0.35em" text-anchor="middle" ` +
    `font-family="Segoe UI, Arial, sans-serif" font-size="72" font-weight="700" fill="#fff">` +
    `${initials}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** Best profile avatar on a story group — newest post first; never story media. */
export function resolveStoryGroupAvatarSrc(
  group: Pick<SocialPostUserGroup, 'userAvatarSrc' | 'posts'> | null | undefined,
): string | null {
  if (!group) {
    return null;
  }
  const fromGroup = resolveProfileAvatarWireSrc(group.userAvatarSrc);
  if (fromGroup) {
    return fromGroup;
  }
  for (let i = group.posts.length - 1; i >= 0; i -= 1) {
    const fromPost = resolveProfileAvatarWireSrc(group.posts[i]?.userAvatarSrc);
    if (fromPost) {
      return fromPost;
    }
  }
  return null;
}

/**
 * Story strip / viewer — live profile for the signed-in user; stored profile URL or initials.
 * Never uses story media as the chip image.
 */
export function resolveStoryUserAvatarSrc(
  groupUserId: string,
  storedAvatarSrc: string | null | undefined,
  currentUserId: string,
  currentUserLiveAvatar: string,
  posts?: readonly SocialPostRecord[],
  userLabel?: string,
): string {
  if (storyUserIdsMatch(groupUserId, currentUserId)) {
    return resolveProfileAvatarDisplay(currentUserLiveAvatar, loadLocalProfileAvatarSrc());
  }
  const fromStored = resolveProfileAvatarWireSrc(storedAvatarSrc);
  if (fromStored) {
    return fromStored;
  }
  if (posts) {
    for (let i = posts.length - 1; i >= 0; i -= 1) {
      const fromPost = resolveProfileAvatarWireSrc(posts[i]?.userAvatarSrc);
      if (fromPost) {
        return fromPost;
      }
    }
  }
  if (userLabel?.trim()) {
    return buildStoryInitialsAvatarSrc(userLabel);
  }
  return GENERIC_PROFILE_AVATAR_SRC;
}
